import type { ChangeEvent, ClipboardEvent, KeyboardEvent, PointerEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Text,
    Textarea,
    Tooltip,
} from '@chakra-ui/react';
import { useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type {
    Control,
    FieldValues,
    Path,
    PathValue,
    UseFormClearErrors,
    UseFormSetError,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { EMOJI_REGEX_GLOBAL, OMS_INVALID_CHAR_REGEX } from '../../utils/validation';
import { messages } from '../forms/useGiftCardFields';
import { GetIcon } from '../icons';
import { generatedPastedText } from './gift-card-utils';

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
const ALLOWED_KEY_WHEN_STUCK = [
    'Alt',
    ...ARROW_KEYS,
    'Backspace',
    'Control',
    'Enter',
    'Delete',
    'Tab',
];

interface MessageState {
    lines: string[];
    currentLineIndex: number;
    charsRemainingInLine: number;
}

interface GiftCardMessageInputProps<TFormValues extends FieldValues> {
    name: Path<TFormValues>;
    maxLines: number;
    maxPerLine: number;
    control: Control<TFormValues>;
    setValue: UseFormSetValue<TFormValues>;
    watch: UseFormWatch<TFormValues>;
    clearErrors: UseFormClearErrors<TFormValues>;
    setError: UseFormSetError<TFormValues>;
    showInternalInfo?: boolean;
}

export const GiftCardMessageInput = <TFormValues extends FieldValues>({
    name,
    maxLines,
    maxPerLine,
    control,
    setValue,
    watch,
    clearErrors,
    setError,
    showInternalInfo = false,
}: GiftCardMessageInputProps<TFormValues>): JSX.Element => {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    });

    const messageValue = (watch(name) || '') as string;

    // Use a local state for immediate UI updates
    const [inputValue, setInputValue] = useState(messageValue);

    const updateValue = useCallback(
        (value: string) => {
            setInputValue(value);
            setValue(name, value as PathValue<TFormValues, Path<TFormValues>>);
        },
        [setValue, name],
    );

    const [messageState, setMessageState] = useState<MessageState>(() => {
        const lines = messageValue.split('\n');
        return {
            lines: lines.filter((str) => str !== ''),
            currentLineIndex: 0,
            charsRemainingInLine: maxPerLine - (lines[0]?.length || 0),
        };
    });

    // Calculate minimum height based on number of lines
    const minHeight = useMemo(() => {
        const baseHeightPerLine = 33; // ~33px per line
        return Math.max(100, baseHeightPerLine * maxLines);
    }, [maxLines]);

    const linesRemaining = useMemo(
        () => maxLines - messageState.lines.filter((str) => str !== '').length,
        [messageState.lines, maxLines],
    );

    const updateLineInfo = useCallback(
        (value: string, selectionStart: number): void => {
            const index = value.substring(0, selectionStart).split('\n').length - 1;
            const splitLines = value.split('\n');
            const lineCharsRemaining = maxPerLine - splitLines[index].length;

            setMessageState({
                lines: splitLines.filter((str) => str !== ''),
                currentLineIndex: index,
                charsRemainingInLine: lineCharsRemaining,
            });
        },
        [maxPerLine],
    );

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            const { value, selectionStart } = event.target as HTMLTextAreaElement;

            // Remove emojis, if they exist
            const valueWithEmojisRemoved = value.replace(EMOJI_REGEX_GLOBAL, '');
            if (valueWithEmojisRemoved.length !== value.length) {
                updateValue(valueWithEmojisRemoved);
                updateLineInfo(valueWithEmojisRemoved, selectionStart);
                return;
            }

            // Split into lines and enforce per-line character limit
            const lines = value.split('\n').slice(0, maxLines);
            const truncatedLines = lines.map((line) => line.slice(0, maxPerLine));
            const truncatedValue = truncatedLines.join('\n');

            // Enforce total character limit
            const totalChars = truncatedValue.replace(/\n/g, '').length;
            const maxTotalChars = maxPerLine * maxLines;

            if (totalChars > maxTotalChars) {
                const lastLineIndex = truncatedLines.length - 1;
                const charsOverLimit = totalChars - maxTotalChars;
                if (lastLineIndex >= 0) {
                    truncatedLines[lastLineIndex] = truncatedLines[lastLineIndex].slice(
                        0,
                        -charsOverLimit,
                    );
                }
                const finalValue = truncatedLines.join('\n');
                updateValue(finalValue);
                updateLineInfo(finalValue, selectionStart);
                return;
            }

            updateValue(truncatedValue);
            updateLineInfo(truncatedValue, selectionStart);
        },
        [maxLines, maxPerLine, updateLineInfo, updateValue],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>): void => {
            const { value, selectionStart, selectionEnd } = event.target as HTMLTextAreaElement;

            // Prevent invalid characters like ~@^*()=+[]{}|"<>/
            if (OMS_INVALID_CHAR_REGEX.test(event.key)) {
                event.preventDefault();
                return;
            }

            const index = value.substring(0, selectionStart).split('\n').length - 1;
            const splitLines = value.split('\n');
            const lineCharsRemaining = maxPerLine - splitLines[index].length;

            setMessageState({
                lines: splitLines.filter((str) => str !== ''),
                currentLineIndex: index,
                charsRemainingInLine: lineCharsRemaining,
            });

            const allowedKeyWhenStuck = ALLOWED_KEY_WHEN_STUCK.includes(event.key);
            const totalCharsUsed = value.replace(/\n/g, '').length;
            const maxTotalChars = maxPerLine * maxLines;

            // No characters left at all
            if (totalCharsUsed >= maxTotalChars && !allowedKeyWhenStuck && !event.ctrlKey) {
                setError(name, {
                    message: 'Your message reached the allowed character limit.',
                });
                event.preventDefault();
                return;
            }

            const currentLineText = splitLines[index];
            const currentLineWords = currentLineText.length ? currentLineText.split(' ') : [''];
            const currentLineLastWord = currentLineWords[currentLineWords.length - 1];
            const previousLineText = index > 0 ? splitLines[index - 1] : '';
            const nextLineText = splitLines[index + 1] ?? '';
            const allTextAfterLastWord = value.substring(selectionStart + 1);
            const allTextBeforeLastWord = value.substring(
                0,
                selectionStart - currentLineLastWord.length,
            );

            // Handle user types enter
            if (event.key === 'Enter' && splitLines.length === maxLines) {
                if (!nextLineText.trim().length && value[selectionStart] === '\n') {
                    event.currentTarget.selectionStart = selectionStart + 1;
                    event.preventDefault();
                    return;
                }

                event.preventDefault();
                setError(name, {
                    message:
                        "You've reached the 4-line limit. Try shortening your message or combining lines.",
                });
                return;
            }

            // Prevent backspace if at beginning of line
            if (
                event.key === 'Backspace' &&
                value[selectionStart - 1] === '\n' &&
                currentLineText.length + previousLineText.length > maxPerLine &&
                selectionStart === selectionEnd
            ) {
                event.preventDefault();
                setError(name, {
                    message:
                        "You've reached the 4-line limit. Try shortening your message or combining lines.",
                });
                return;
            }

            // Prevent delete at end of line
            if (
                event.key === 'Delete' &&
                value[selectionStart] === '\n' &&
                currentLineText.length + nextLineText.length >= maxPerLine &&
                selectionStart === selectionEnd
            ) {
                event.preventDefault();
                setError(name, {
                    message:
                        "You've reached the 4-line limit. Try shortening your message or combining lines.",
                });
                return;
            }

            // Handle no characters left on current line
            if (lineCharsRemaining <= 0 && !allowedKeyWhenStuck && !event.ctrlKey) {
                if (splitLines.length === maxLines) {
                    if (!nextLineText.trim().length && index + 1 < maxLines) {
                        const newValue = allTextBeforeLastWord
                            .trim()
                            .concat(`\n${currentLineLastWord}`)
                            .concat(`${allTextAfterLastWord.trimRight()}`);

                        setInputValue(newValue);
                        event.currentTarget.selectionStart = selectionStart + 1;
                        event.currentTarget.selectionEnd = selectionEnd + 1;
                        return;
                    }

                    setError(name, {
                        message:
                            "You've reached the 4-line limit. Try shortening your message or combining lines.",
                    });
                    event.preventDefault();
                    return;
                }

                if (nextLineText.trim().length) {
                    setError(name, { message: 'Already has text in next line' });
                    event.preventDefault();
                    return;
                }

                if (currentLineLastWord.length >= maxPerLine) {
                    setError(name, {
                        message:
                            "You've reached the 4-line limit. Try shortening your message or combining lines.",
                    });
                    event.preventDefault();
                    return;
                }

                if (event.key === ' ') {
                    const newValue = value.trim().concat('\n');
                    setInputValue(newValue);
                    setMessageState({
                        lines: newValue.split('\n').filter((str) => str !== ''),
                        currentLineIndex: index + 1,
                        charsRemainingInLine: maxPerLine,
                    });
                    event.preventDefault();
                    return;
                }

                const newValue = allTextBeforeLastWord.trim().concat(`\n${currentLineLastWord}`);
                setInputValue(newValue);
                setMessageState({
                    lines: newValue.split('\n').filter((str) => str !== ''),
                    currentLineIndex: index + 1,
                    charsRemainingInLine: maxPerLine - currentLineLastWord.length,
                });
            }

            clearErrors(name);
        },
        [clearErrors, maxLines, maxPerLine, name, setError],
    );

    const handleKeyUp = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>): void => {
            const target = event.target as HTMLTextAreaElement;
            updateLineInfo(target.value, target.selectionStart);
        },
        [updateLineInfo],
    );

    const handleClick = useCallback(
        (event: PointerEvent<HTMLTextAreaElement>): void => {
            const target = event.target as HTMLTextAreaElement;
            updateLineInfo(target.value, target.selectionStart);
        },
        [updateLineInfo],
    );

    const handlePaste = useCallback(
        (event: ClipboardEvent<HTMLTextAreaElement>): void => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData('text');
            const target = event.target as HTMLTextAreaElement;
            const { selectionStart, selectionEnd, value } = target;

            const { newValue, textBeforeCursor, cleanPastedText } = generatedPastedText(
                pastedText,
                maxPerLine,
                maxLines,
                selectionStart,
                selectionEnd,
                value,
            );

            // Update state
            updateValue(newValue);
            updateLineInfo(newValue, textBeforeCursor.length + cleanPastedText.length);
            clearErrors(name);
        },
        [maxPerLine, maxLines, name, clearErrors, updateLineInfo, updateValue],
    );

    return (
        <FormControl isInvalid={!!error}>
            <FormLabel htmlFor={name}>
                <Flex>
                    <FormattedMessage
                        defaultMessage="Your Message (optional)"
                        id="use_gift_card_fields.label.gift_card_message"
                    />
                    <Tooltip
                        label={
                            <Text size="small">
                                <FormattedMessage {...messages.giftCardMessageTooltip} />
                            </Text>
                        }
                    >
                        <div className="flex" data-testid="gift-card-message-tooltip">
                            <GetIcon name="info" size="md" />
                        </div>
                    </Tooltip>
                </Flex>
            </FormLabel>
            <Textarea
                {...field}
                id={name}
                data-testid="gift-card-message"
                variant="slt-outline"
                resize="none"
                minHeight={`${minHeight}px`}
                value={inputValue}
                onChange={(event) => {
                    handleChange(event);
                }}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onClick={handleClick}
                onPaste={handlePaste}
            />
            {showInternalInfo && (
                <Flex direction="column" justifyContent="space-between" marginTop={2}>
                    <FormHelperText>
                        {messageState.charsRemainingInLine} characters remaining in line{' '}
                        {messageState.currentLineIndex + 1} • {linesRemaining} lines remaining
                    </FormHelperText>
                    {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
                </Flex>
            )}
        </FormControl>
    );
};

GiftCardMessageInput.defaultProps = {
    showInternalInfo: false,
};
